import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { sb } from '../modules/state';

const API_BASE = import.meta.env.VITE_API_BASE;

const ADMIN_EMAILS = ['francois@222fasting.com', 'admin@222fasting.com'];

interface Profile {
  subscription_status?: string | null;
  insulin_gate_passed?: boolean;
  current_protocol?: string;
  protocol_start_date?: string;
  name?: string;
  last_name?: string;
  birthdate?: string | null;
  trial_used_at?: string | null;
}

interface AuthContextType {
  currentUser: any;
  profile: Profile | null;
  subscriptionStatus: string | null;
  signUp: (email: string, password: string, name: string, lastName: string, birthdate: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<any>;
  loading: boolean;
  isSubscribed: boolean;
  trialUsed: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [hasSupabase, setHasSupabase] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [trialUsed, setTrialUsed] = useState(false);

  const fetchProfile = (userId: string) => {
    return sb.from('profiles').select('subscription_status').eq('id', userId).single();
  };

  const computeIsSubscribed = (userEmail: string, subscriptionStatus: string | null) => {
    return ADMIN_EMAILS.includes(userEmail) ||
      subscriptionStatus === 'trial' ||
      subscriptionStatus === 'on_trial' ||
      subscriptionStatus === 'active';
  };

  useEffect(() => {
    if (!sb) {
      setHasSupabase(false);
      setLoading(false);
      return;
    }
    setHasSupabase(true);

    sb.auth.getSession().then(({ data: { session } }) => {
      const user = session?.user;

      if (user) {
        sb.auth.getUser().then(({ data: { user: verifiedUser }, error }) => {
          if (error || !verifiedUser) {
            sb.auth.signOut();
            setCurrentUser(null);
            localStorage.removeItem('222f_age_confirmed');
            setLoading(false);
            return;
          }

          setCurrentUser(verifiedUser);

          fetchProfile(verifiedUser.id).then(({ data }) => {
            const subscribed = computeIsSubscribed(verifiedUser.email, data?.subscription_status);
            setIsSubscribed(subscribed);
            setSubscriptionStatus(data?.subscription_status ?? null);
            setTrialUsed(!!data?.trial_used_at);

            if (data) {
              setProfile(data);
            }
            setLoading(false);
          }).catch(() => {
            // No profile exists - new users are on trial by default
            const subscribed = true; // All new users get trial access
            setIsSubscribed(subscribed);
            setSubscriptionStatus('trial');
            setTrialUsed(false);
            setLoading(false);
          });
        });
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = sb.auth.onAuthStateChange((_event, session) => {
      const user = session?.user;
      setCurrentUser(user);

      if (user) {
        fetchProfile(user.id).then(({ data }) => {
          const subscribed = computeIsSubscribed(user.email, data?.subscription_status);
          setIsSubscribed(subscribed);
          setSubscriptionStatus(data?.subscription_status ?? null);
          setTrialUsed(!!data?.trial_used_at);

          if (data) {
            setProfile(data);
          }
          setLoading(false);
        }).catch(() => {
          // No profile exists - new users are on trial by default
          setIsSubscribed(true);
          setSubscriptionStatus('trial');
          setTrialUsed(false);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signUp = (email: string, password: string, name: string, lastName: string, birthdate: string) => {
    if (!sb) {
      console.error('[222fasting] Supabase not configured');
      return Promise.resolve({ error: new Error('Supabase not configured') });
    }
    return sb.auth.signUp({
      email,
      password,
      options: {
        data: {
          email,
          name,
          last_name: lastName,
          birthdate,
        }
      },
    }).then((response) => {
      if (response.data?.user) {
        setCurrentUser(response.data.user);
      }
      return response;
    });
  };

  const signIn = (email: string, password: string) => {
    if (!sb) {
      console.error('[222fasting] Supabase not configured');
      return Promise.resolve({ error: new Error('Supabase not configured') });
    }
    return sb.auth.signInWithPassword({
      email,
      password,
    }).then((response) => {
      if (response.error) {
        return response;
      }
      if (!response.data?.session) {
        return { error: { message: 'Invalid email or password' } };
      }
      setCurrentUser(response.data.session.user);
      fetchProfile(response.data.session.user.id).then(({ data }) => {
        if (data) {
          setProfile(data);
          setSubscriptionStatus(data.subscription_status ?? null);
        }
      }).catch(() => {});
      return response;
    });
  };

  const signOut = () => {
    if (!sb) {
      console.error('[222fasting] Supabase not configured');
      return Promise.resolve();
    }
    return sb.auth.signOut().then(() => {
      setCurrentUser(null);
      localStorage.removeItem('222f_age_confirmed');
    });
  };

  const resetPassword = (email: string) => {
    if (!sb) {
      console.error('[222fasting] Supabase not configured');
      return Promise.resolve({ error: new Error('Supabase not configured') });
    }
    return sb.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });
  };

  return (
    <AuthContext.Provider value={{ currentUser, profile, subscriptionStatus, signUp, signIn, signOut, resetPassword, loading, isSubscribed, trialUsed }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
